/**
 * A published race played on the live race page in the browser (race-replay.ts), with a player:
 * play, pause, back or on a minute, 1×, 2× or 3× speed, and a scrubber. Returns what
 * useLiveChartData returns for a live race, so the page's views work unchanged, and the player.
 * The page's own clock does not run on it (clockAt is null): the replay's time is the time.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useChartData } from "../../hooks/useChartData";
import type { LiveInfo, LiveRaceMeta, UseLiveChartData } from "../../hooks/useLiveChartData";
import type { RaceChartData } from "@shared/types";
import { fetchRacePackage } from "../../lib/package-api";
import type { RaceAnalysisPackage } from "@shared/race-analysis-package/types";
import type { RaceControlMessage } from "../chart/RaceControl";
import { controlAt, flagAt, pitLaneAt, replayControl, replayControlFromLog, replayFlags, replayPitCalls, type RaceControlLogEntry } from "./replay-control";
import { api } from "../../lib/api";
import { clockText, lapEndsBy, replayCut, replayTime, replayTimeline, seekClock, type ReplayClock, type ReplaySpeed, type ReplayTimeline } from "./race-replay";

/** The address of a race's replay: /live/race-<race id>. */
export const RACE_REPLAY_PREFIX = "race-";
export const raceReplayId = (slug: string | undefined): string | null => (slug && slug.startsWith(RACE_REPLAY_PREFIX) ? slug.slice(RACE_REPLAY_PREFIX.length) : null);

export interface ReplayPlayer {
  /** Race time now (s), the replay's length (s: the last car takes the flag) and the scheduled length (s: the time to go runs out). */
  timeS: number;
  lengthS: number;
  scheduledS: number;
  playing: boolean;
  speed: ReplaySpeed;
  play: () => void;
  pause: () => void;
  setSpeed: (s: ReplaySpeed) => void;
  /** Move by this many race seconds (negative: back). */
  step: (s: number) => void;
  seek: (t: number) => void;
  /** Race time at this device's time (ms), for the map's frames. */
  timeAt: (wallMs: number) => number;
  /** The whole race and its clock (the map places each car from its timing). */
  full: RaceChartData;
  timeline: ReplayTimeline;
  /** Race control's messages by now, newest first (replay-control.ts); null while the race's package loads or without one. */
  control: RaceControlMessage[] | null;
}

/** How often the replay's clock moves the page (ms). */
const TICK_MS = 500;

export function useRaceReplay(raceId: string | null, startS = 0): UseLiveChartData & { player: ReplayPlayer | null } {
  const { data: full, annotations: fullAnn, raceMeta, isLoading, error } = useChartData(raceId ?? undefined);
  const tl = useMemo(() => (full ? replayTimeline(full) : null), [full]);
  const [clock, setClock] = useState<ReplayClock>(() => ({ atS: startS, wallMs: Date.now(), speed: 1, playing: true }));
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!clock.playing) return;
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, [clock.playing]);
  const lengthS = tl?.lengthS ?? 0;
  const t = tl ? replayTime(clock, now, lengthS) : 0;
  // The chart changes only when a lap ends: cut once per lap end passed.
  const passed = tl ? lapEndsBy(tl, t) : 0;
  const cut = useMemo(() => (full && tl ? replayCut(full, fullAnn, tl, t) : null), [full, fullAnn, tl, passed]); // eslint-disable-line react-hooks/exhaustive-deps
  // Race control from the race's published package, shown as the clock passes each message.
  const [pkg, setPkg] = useState<Pick<RaceAnalysisPackage, "laps" | "events"> | null>(null);
  useEffect(() => {
    setPkg(null);
    if (!raceId) return;
    const ac = new AbortController();
    fetchRacePackage(raceId, { signal: ac.signal })
      .then(({ pkg: p }) => setPkg({ laps: p.laps, events: p.events }))
      .catch(() => undefined);
    return () => ac.abort();
  }, [raceId]);
  // Race control's full log when the race has one; else what the package keeps (flags, pit-lane calls, penalties).
  const [log, setLog] = useState<RaceControlLogEntry[] | null>(null);
  useEffect(() => {
    setLog(null);
    if (!raceId) return;
    const ac = new AbortController();
    api
      .get<{ messages?: RaceControlLogEntry[] }>(`/races/${encodeURIComponent(raceId)}/control`, { signal: ac.signal })
      .then((r) => setLog(Array.isArray(r.messages) ? r.messages : []))
      .catch(() => setLog([]));
    return () => ac.abort();
  }, [raceId]);
  const ctl = useMemo(() => {
    if (!full || !tl || log === null) return null;
    if (log.length) return replayControlFromLog(log, pkg, full, tl);
    return pkg ? replayControl(pkg, full, tl) : null;
  }, [log, pkg, full, tl]);
  // The flag from the race's flag times when it has them (a lap's flag only says the caution touched it).
  const flags = useMemo(() => (pkg && full && tl ? replayFlags(pkg, full, tl) : []), [pkg, full, tl]);
  const flagNow = flagAt(flags, t);
  // The pit lane by the second from the race's calls (the chart has them by the leader's lap).
  const pitCalls = useMemo(() => (pkg && full && tl && full.pitLane ? replayPitCalls(pkg, full, tl) : []), [pkg, full, tl]);
  let pitPassed = 0;
  while (pitPassed < pitCalls.length && pitCalls[pitPassed].tS <= t) pitPassed++;
  const cutData = useMemo(
    () => (cut && pitCalls.length ? { ...cut.data, pitLane: pitLaneAt(pitCalls, full?.pitLane, cut.leaderLap, t) } : (cut?.data ?? null)),
    [cut, pitCalls, pitPassed] // eslint-disable-line react-hooks/exhaustive-deps
  );
  let shown = 0;
  if (ctl) while (shown < ctl.length && ctl[shown].tS <= t) shown++;
  const control = useMemo(() => (ctl ? controlAt(ctl, t) : null), [ctl, shown]); // eslint-disable-line react-hooks/exhaustive-deps
  // At the flag the clock stops.
  useEffect(() => {
    if (tl && clock.playing && t >= lengthS) setClock((c) => ({ ...c, atS: lengthS, wallMs: Date.now(), playing: false }));
  }, [tl, clock.playing, t, lengthS]);

  const at = useCallback((f: (c: ReplayClock, nowMs: number) => ReplayClock) => {
    const n = Date.now();
    setNow(n);
    setClock((c) => f(c, n));
  }, []);
  const player: ReplayPlayer | null = tl
    ? {
        timeS: t,
        lengthS,
        scheduledS: tl.scheduledS,
        playing: clock.playing,
        speed: clock.speed,
        play: () => at((c, n) => ({ ...seekClock(c, replayTime(c, n, lengthS) >= lengthS ? 0 : replayTime(c, n, lengthS), n, lengthS), playing: true })),
        pause: () => at((c, n) => ({ ...seekClock(c, replayTime(c, n, lengthS), n, lengthS), playing: false })),
        setSpeed: (speed) => at((c, n) => ({ ...seekClock(c, replayTime(c, n, lengthS), n, lengthS), speed })),
        step: (s) => at((c, n) => seekClock(c, replayTime(c, n, lengthS) + s, n, lengthS)),
        seek: (x) => at((c, n) => seekClock(c, x, n, lengthS)),
        timeAt: (wallMs) => replayTime(clock, wallMs, lengthS),
        full: full!,
        timeline: tl,
        control,
      }
    : null;

  const race: LiveRaceMeta | null = raceMeta ? { id: raceMeta.id, name: raceMeta.name, date: raceMeta.date, track: raceMeta.track, series: raceMeta.series, season: raceMeta.season } : null;
  // Finished once the leader takes the checkered flag; the replay runs on until the last car has.
  const ended = !!tl && t >= tl.finishS;
  const live: LiveInfo | null =
    raceId && cut && raceMeta
      ? {
          slug: `${RACE_REPLAY_PREFIX}${raceId}`,
          updatedAt: null,
          waiting: null,
          delayed: false,
          ended,
          eventName: raceMeta.name,
          sessionName: null,
          track: raceMeta.track,
          status: {
            leaderLap: cut.leaderLap,
            carCount: Object.keys(cut.data.cars).length,
            flag: ended ? "Checkered" : (flagNow ?? (cut.caution ? "Yellow" : "Green")),
            runningRaceTime: clockText(t),
            timeToGo: clockText(Math.max(0, (tl?.scheduledS ?? lengthS) - t)),
            warnings: [],
          },
        }
      : null;
  return {
    data: cutData,
    annotations: cut?.annotations ?? null,
    race,
    live,
    checkedAt: null,
    clockAt: null,
    error: error ? error.message : null,
    isLoading: !!raceId && isLoading,
    saver: false,
    streaming: false,
    refresh: () => undefined,
    player,
  };
}
